import React from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core'

import { fetchPackages, tierFeatureRows, PACKAGE_STATUS } from '../helpers/packageTiers'

/**
 * Package picker for Create QCS organisation.
 *
 * Shows the packages defined in the Package builder as plan cards, each listing
 * exactly the features it includes — the way a streaming service lays out its
 * plans. The operator picks one, and its id is stamped on the organisation at
 * creation. Clicking the chosen card again clears the selection.
 */

const styles = () => ({
  wrap: {
    marginTop: 16,
    fontFamily: 'var(--careco-font)',
  },
  label: {
    display: 'block',
    fontSize: 14,
    lineHeight: '17px',
    fontFamily: 'Lato, sans-serif',
    color: 'var(--careco-text-primary)',
    marginBottom: 10,
  },
  req: { color: 'var(--careco-error)', marginLeft: 2 },

  grid: {
    // Single horizontal row of fixed-width cards that scrolls sideways when
    // there are more than fit. Column gap is applied inline (JSS drops `gap`).
    // Vertical padding leaves room for the selected card's shadow / hover lift,
    // which the scroll container would otherwise clip.
    display: 'flex',
    overflowX: 'auto',
    padding: '6px 2px 18px',
  },
  card: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    // Fixed width so the cards keep their size and scroll rather than squeeze.
    flex: '0 0 280px',
    width: 280,
    textAlign: 'left',
    padding: '16px 14px',
    border: '1.5px solid var(--careco-border)',
    borderRadius: 'var(--careco-rounded-md, 12px)',
    background: 'var(--careco-surface)',
    fontFamily: 'inherit',
    cursor: 'pointer',
    transition: 'border-color 140ms ease, box-shadow 140ms ease, transform 140ms ease',
    '&:hover': { borderColor: 'var(--careco-primary)', transform: 'translateY(-2px)' },
  },
  cardOn: {
    borderColor: 'var(--careco-primary)',
    boxShadow: '0 0 0 1px var(--careco-primary) inset, 0 6px 18px rgba(0,0,0,0.06)',
  },
  cardReadonly: {
    cursor: 'default',
    '&:hover': { borderColor: 'var(--careco-primary)', transform: 'none' },
    '&:disabled': {
      opacity: 1,
      color: 'inherit',
    },
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    paddingRight: 26,
    marginBottom: 16,
  },
  chip: {
    flexShrink: 0,
    width: 34,
    height: 34,
    marginRight: 12,
    borderRadius: 'var(--careco-rounded-sm, 8px)',
    background: 'var(--careco-primary-light)',
    color: 'var(--careco-primary)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headText: { minWidth: 0 },
  name: {
    margin: 0,
    fontSize: 14.5,
    fontWeight: 700,
    lineHeight: 1.25,
    color: 'var(--careco-text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  level: {
    margin: '2px 0 0',
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--careco-text-muted)',
  },
  radio: {
    position: 'absolute',
    top: 16,
    right: 12,
    width: 18,
    height: 18,
    boxSizing: 'border-box',
    borderRadius: '50%',
    border: '2px solid var(--careco-border)',
    background: 'var(--careco-surface)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'border-color 140ms ease',
  },
  radioOn: { borderColor: 'var(--careco-primary)' },
  radioDot: { width: 8, height: 8, borderRadius: '50%', background: 'var(--careco-primary)' },

  features: { display: 'flex', flexDirection: 'column', flex: 1 },
  feature: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 16,
    '&:first-of-type': { marginTop: 0 },
  },
  featIcon: {
    flexShrink: 0,
    width: 30,
    height: 30,
    marginRight: 16,
    borderRadius: 'var(--careco-rounded-sm, 8px)',
    background: 'var(--careco-primary-light)',
    color: 'var(--careco-primary)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  featText: { minWidth: 0 },
  featTitle: {
    display: 'block',
    fontSize: 13.5,
    fontWeight: 700,
    lineHeight: 1.35,
    color: 'var(--careco-text-primary)',
  },
  featDetail: {
    display: 'block',
    marginTop: 4,
    fontSize: 12.5,
    lineHeight: '17px',
    color: 'var(--careco-text-muted)',
  },
  empty: {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    fontSize: 12.5,
    lineHeight: '18px',
    color: 'var(--careco-text-muted)',
  },
  // Whole-picker empty state (no packages defined yet).
  noPackages: {
    padding: '18px 16px',
    border: '1px dashed var(--careco-border)',
    borderRadius: 'var(--careco-rounded-md, 12px)',
    fontSize: 13.5,
    lineHeight: '20px',
    color: 'var(--careco-text-muted)',
  },
  error: { margin: '10px 0 0', fontSize: 12, color: 'var(--careco-error)' },
})

const Glyph = ({ paths, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    {paths.map((d, i) => (
      <path
        key={i}
        d={d}
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ))}
  </svg>
)

Glyph.propTypes = { paths: PropTypes.array, size: PropTypes.number }

class TierSelect extends React.Component {
  state = {
    packages: [],
    loading: true,
  }

  componentDidMount() {
    this.loadLivePackages()
  }

  loadLivePackages = () => {
    this.setState({ loading: true })
    fetchPackages()
      .then(list => {
        // Only Live packages with at least one feature: an empty package grants
        // nothing, so it is not a valid choice for a new organisation.
        const packages = (list || []).filter(
          pkg => pkg.status === PACKAGE_STATUS.LIVE && tierFeatureRows(pkg).length > 0,
        )
        this.setState({ packages, loading: false })
      })
      .catch(() => this.setState({ packages: [], loading: false }))
  }

  render() {
    const { classes, value, onChange, error, hideLabel, selectedOnly } = this.props
    const { packages, loading } = this.state
    const valueKey = value == null ? '' : String(value)

    if (loading) {
      return (
        <div className={classes.wrap}>
          {!hideLabel && (
            <span className={classes.label}>
              Choose a package<span className={classes.req}>*</span>
            </span>
          )}
          <div className={classes.noPackages}>Loading packages…</div>
        </div>
      )
    }

    // Manage Limits: show only the assigned package (or a clear empty state).
    const visiblePackages = selectedOnly
      ? valueKey
        ? packages.filter(pkg => String(pkg.id) === valueKey)
        : []
      : packages

    if (visiblePackages.length === 0) {
      return (
        <div className={classes.wrap}>
          {!hideLabel && (
            <span className={classes.label}>
              Choose a package<span className={classes.req}>*</span>
            </span>
          )}
          <div className={classes.noPackages}>
            {selectedOnly
              ? valueKey
                ? 'This organisation’s package is no longer available in the live catalogue.'
                : 'No package assigned to this organisation.'
              : 'No live packages yet. Create one in the Package builder and push it to Live, then it will appear here to choose.'}
          </div>
        </div>
      )
    }

    return (
      <div className={classes.wrap}>
        {!hideLabel && (
          <span className={classes.label}>
            Choose a package<span className={classes.req}>*</span>
          </span>
        )}

        <div
          className={classes.grid}
          role={selectedOnly ? undefined : 'radiogroup'}
          aria-label={selectedOnly ? undefined : 'Package'}
          style={{ columnGap: 16, rowGap: 16 }}>
          {visiblePackages.map(pkg => {
            const rows = tierFeatureRows(pkg)
            const selected = valueKey === String(pkg.id)
            return (
              <button
                key={pkg.id}
                type="button"
                role={selectedOnly ? undefined : 'radio'}
                aria-checked={selectedOnly ? undefined : selected}
                disabled={selectedOnly}
                className={`${classes.card} ${selected ? classes.cardOn : ''} ${
                  selectedOnly ? classes.cardReadonly : ''
                }`}
                onClick={
                  selectedOnly
                    ? undefined
                    : () => onChange(selected ? '' : pkg.id)
                }>
                {!selectedOnly && (
                  <span
                    className={`${classes.radio} ${selected ? classes.radioOn : ''}`}
                    aria-hidden="true">
                    {selected && <span className={classes.radioDot} />}
                  </span>
                )}

                <div className={classes.head}>
                  <span className={classes.headText}>
                    <p className={classes.name}>{pkg.name || 'Untitled package'}</p>
                  </span>
                </div>

                {rows.length ? (
                  <div className={classes.features}>
                    {rows.map(row => (
                      <div className={classes.feature} key={row.key}>
                        <span className={classes.featIcon}>
                          <Glyph paths={row.icon} size={15} />
                        </span>
                        <span className={classes.featText}>
                          <span className={classes.featTitle}>{row.title}</span>
                          <span className={classes.featDetail}>{row.detail}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className={classes.empty}>No features switched on for this package.</span>
                )}
              </button>
            )
          })}
        </div>

        {error && !selectedOnly && <p className={classes.error}>Please choose a package.</p>}
      </div>
    )
  }
}

TierSelect.propTypes = {
  classes: PropTypes.object,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  error: PropTypes.bool,
  // When true, the built-in "Choose a package" label is omitted (the host
  // supplies its own heading, e.g. the Monthly limits dialog).
  hideLabel: PropTypes.bool,
  // Manage Limits: show the assigned package only — no switching / clearing.
  selectedOnly: PropTypes.bool,
}

TierSelect.defaultProps = {
  selectedOnly: false,
}

export default withStyles(styles)(TierSelect)
